import gql from 'graphql-tag';
import { useQuery, useMutation, useResult } from '@vue/apollo-composable';
import { computed, ref } from 'vue';
import { cloneDeep } from 'lodash-es';
import { useAccountInfo } from '@models/account/useAccountInfo';

const templateSchema = `
    {
        id
        name
        description
        pcThumbnail
        pcWidgets
        pcWidgetLayout
        mobileThumbnail
        mobileWidgets
        mobileWidgetLayout
        deviceType
        type
        tag
        area
        sort
        recommendationLocation
        updateTime
    }
`;

enum EngageV4DeviceEnum {
    'PC',
    'MOBILE'
}

export type ExperienceTemplateV4 = {
    id: string;
    profileId?: string;
    name: string;
    description: string;
    pcWidgets?: Object;
    pcWidgetLayout?: string;
    mobileWidgets?: Object;
    mobileWidgetLayout?: string;
    mobileThumbnail?: string;
    pcThumbnail?: string;
    deviceType?: EngageV4DeviceEnum[];
    type?: string;
    tag?: string;
    area?: 'JP' | 'EN';
    sort?: number | string;
    sync?: boolean;
    i18nName?: string;
    i18nDesc?: string;
    tags?: any;
    isfromEC?: boolean;
    recommendationLocation?: string;
    showTags?: string[]; // 模板显示的所有标签数组
    isPrivate?: boolean;
    updateTime?: number;
};

const OFFICIAL_TEMPLATE_QUERY = gql`
    query getAllOfficialEngageV4Template($type: EngageV4TypeEnum!) {
        getAllOfficialEngageV4Template(type: $type)${templateSchema}
    }
`;

const OWN_TEMPLATE_QUERY = gql`
    query getProfileEngageV4Template($type: EngageV4TypeEnum!) {
        getProfileEngageV4Template(type: $type) ${templateSchema}
    }
`;

// update own template(name, description)
const OWN_TEMPLATE_UPDATE_MUTATION = gql`
    mutation updateEngageV4Template($input: EngageV4TemplateInput!) {
        updateEngageV4Template(input: $input)
    }
`;

// delete own template
const OWN_TEMPLATE_DELETE_MUTATION = gql`
    mutation deleteEngageV4Template($id: ID!) {
        deleteEngageV4Template(id: $id)
    }
`;
const getOfficalPopup = () => {
    const {
        result: officialPopupTemplatesV4Result,
        loading: officialPopupTemplatesV4Loading,
        onResult: officialPopupTemplatesV4OnResult
    } = useQuery<{ getAllOfficialEngageV4Template: ExperienceTemplateV4[] }>(
        OFFICIAL_TEMPLATE_QUERY,
        {
            type: 'POPUP'
        },
        {
            fetchPolicy: 'cache-first' //cache-and-network
        }
    );
    return {
        officialPopupTemplatesV4Result,
        officialPopupTemplatesV4Loading,
        officialPopupTemplatesV4OnResult
    };
};

const getOwnPopup = () => {
    const {
        result: ownPopupTemplatesV4Result,
        loading: ownPopupTemplatesV4Loading,
        onResult: ownPopupTemplatesV4OnResult
    } = useQuery<{ getProfileEngageV4Template: ExperienceTemplateV4[] }>(
        OWN_TEMPLATE_QUERY,
        {
            type: 'POPUP'
        },
        {
            fetchPolicy: 'cache-and-network'
        }
    );
    return { ownPopupTemplatesV4Result, ownPopupTemplatesV4Loading, ownPopupTemplatesV4OnResult };
};

const getOfficialStickybar = () => {
    const {
        result: officialStickybarTemplatesV4Result,
        loading: officialStickybarTemplatesV4Loading,
        onResult: officialStickybarTemplatesV4OnResult
    } = useQuery<{ getAllOfficialEngageV4Template: ExperienceTemplateV4[] }>(
        OFFICIAL_TEMPLATE_QUERY,
        {
            type: 'STICKY_BAR'
        },
        {
            fetchPolicy: 'cache-first' // cache-and-network
        }
    );
    return {
        officialStickybarTemplatesV4Result,
        officialStickybarTemplatesV4Loading,
        officialStickybarTemplatesV4OnResult
    };
};

const getOwnStickybar = () => {
    const {
        result: ownStickybarTemplatesV4Result,
        loading: ownStickybarTemplatesV4Loading,
        onResult: ownStickybarTemplatesV4OnResult
    } = useQuery<{ getProfileEngageV4Template: ExperienceTemplateV4[] }>(
        OWN_TEMPLATE_QUERY,
        {
            type: 'STICKY_BAR'
        },
        {
            fetchPolicy: 'cache-and-network' //cache-and-network
        }
    );
    return {
        ownStickybarTemplatesV4Result,
        ownStickybarTemplatesV4Loading,
        ownStickybarTemplatesV4OnResult
    };
};
export const formatTemplateFunc = (
    list: ExperienceTemplateV4[],
    area: string | null,
    language: string,
    isPrivate: boolean
) => {
    return cloneDeep(list)
        .filter(
            (item: ExperienceTemplateV4) => !area || item?.area?.includes(area?.toLocaleUpperCase())
        )
        .map(r => {
            let tag, name, description;
            try {
                tag = r.tag && JSON.parse(r.tag);
                name = JSON.parse(r.name)?.[language] ?? r.name;
                description = JSON.parse(r.description)?.[language] ?? r.description;
            } catch (e) {
                tag = r.tag;
                name = r.name;
                description = r.description;
            }
            r.isPrivate = isPrivate;
            r['i18nName'] = name;
            r['i18nDesc'] = description;
            r.isfromEC = false;
            r.tags = {
                industry: tag?.industry ?? [],
                purpose: tag?.purpose ?? [],
                location: r.recommendationLocation,
                priority: r.sort !== undefined && r.sort !== '' ? r.sort : 1,
                test: false,
                type: 'Engagement',
                abtestType: ''
            };
            r.showTags = [...r.tags?.purpose, ...r.tags?.industry, 'Engagement'];
            return r;
        });
};

export const useAllTemplates = () => {
    const { account } = useAccountInfo();
    const currentArea = computed(() => {
        return /(ptmind|ptengine)\.com/.test(location.host) ? 'en' : 'jp';
    });
    const area = computed(() => {
        return account.value ? account.value?.area?.toLocaleLowerCase() : currentArea.value;
    });
    const language = computed(() => {
        return account.value ? account.value?.language?.toLocaleLowerCase() : currentArea.value;
    });
    const {
        officialPopupTemplatesV4Result,
        officialPopupTemplatesV4Loading,
        officialPopupTemplatesV4OnResult
    } = getOfficalPopup();

    const {
        officialStickybarTemplatesV4Result,
        officialStickybarTemplatesV4Loading,
        officialStickybarTemplatesV4OnResult
    } = getOfficialStickybar();

    const { ownPopupTemplatesV4Result, ownPopupTemplatesV4Loading, ownPopupTemplatesV4OnResult } =
        getOwnPopup();
    const {
        ownStickybarTemplatesV4Result,
        ownStickybarTemplatesV4Loading,
        ownStickybarTemplatesV4OnResult
    } = getOwnStickybar();
    const officialPopResult = useResult(
        officialPopupTemplatesV4Result,
        null,
        data => data.getAllOfficialEngageV4Template
    );
    const officialStickyResult = useResult(
        officialStickybarTemplatesV4Result,
        null,
        data => data.getAllOfficialEngageV4Template
    );
    const ownPopResult = useResult(
        ownPopupTemplatesV4Result,
        null,
        data => data.getProfileEngageV4Template
    );
    const ownStickyResult = useResult(
        ownStickybarTemplatesV4Result,
        null,
        data => data.getProfileEngageV4Template
    );
    const officialTemplates = computed(() => {
        if (officialPopResult.value && officialStickyResult.value) {
            return formatTemplateFunc(
                [...officialPopResult.value, ...officialStickyResult.value],
                area.value,
                language.value,
                false
            );
        }
        return [];
    });

    const ownTemplates = computed(() => {
        if (ownPopResult.value && ownStickyResult.value) {
            return formatTemplateFunc(
                [...ownPopResult.value, ...ownStickyResult.value],
                null,
                language.value,
                true
            );
        }
        return [];
    });

    // update own templates
    const {
        mutate: updateOwnTemplateMutate,
        loading: updateOwnTemplateMutateLoading,
        error: updateOwnTemplateMutateError
    } = useMutation(OWN_TEMPLATE_UPDATE_MUTATION);

    const updateOwnTemplate = (params: ExperienceTemplateV4, onSuccess?: () => void) => {
        updateOwnTemplateMutate(
            {
                input: params
            },
            {
                update: () => {
                    getOwnPopup();
                    getOwnStickybar();
                    onSuccess && typeof onSuccess === 'function' && onSuccess();
                }
            }
        );
    };

    // delete template
    const {
        mutate: deleteOwnTemplateMutate,
        loading: deleteOwnTemplateMutateLoading,
        error: deleteOwnTemplateMutateError
    } = useMutation(OWN_TEMPLATE_DELETE_MUTATION);

    const deleteOwnTemplate = (id: string, onSuccess?: () => void) => {
        deleteOwnTemplateMutate(
            { id },
            {
                update: () => {
                    getOwnPopup();
                    getOwnStickybar();
                    onSuccess && typeof onSuccess === 'function' && onSuccess();
                }
            }
        );
    };

    return {
        officialTemplates,
        ownTemplates,
        officialTemplatesLoading:
            officialPopupTemplatesV4Loading || officialStickybarTemplatesV4Loading,
        ownTemplatesLoading: ownPopupTemplatesV4Loading || ownStickybarTemplatesV4Loading,
        updateOwnTemplate,
        deleteOwnTemplate,
        officialPopupTemplatesV4OnResult,
        officialStickybarTemplatesV4OnResult,
        ownPopupTemplatesV4OnResult,
        ownStickybarTemplatesV4OnResult
    };
};

export const useAllOfficialTemplates = () => {
    const { account } = useAccountInfo();
    const currentArea = computed(() => {
        return /(ptmind|ptengine)\.com/.test(location.host) ? 'en' : 'jp';
    });
    const area = computed(() => {
        return account.value ? account.value?.area?.toLocaleLowerCase() : currentArea.value;
    });
    const language = computed(() => {
        return account.value ? account.value?.language?.toLocaleLowerCase() : currentArea.value;
    });
    const {
        officialPopupTemplatesV4Result,
        officialPopupTemplatesV4Loading,
        officialPopupTemplatesV4OnResult
    } = getOfficalPopup();

    const {
        officialStickybarTemplatesV4Result,
        officialStickybarTemplatesV4Loading,
        officialStickybarTemplatesV4OnResult
    } = getOfficialStickybar();

    const officialPopResult = useResult(
        officialPopupTemplatesV4Result,
        null,
        data => data.getAllOfficialEngageV4Template
    );
    const officialStickyResult = useResult(
        officialStickybarTemplatesV4Result,
        null,
        data => data.getAllOfficialEngageV4Template
    );
    const officialTemplates = computed(() => {
        if (officialPopResult.value && officialStickyResult.value) {
            return formatTemplateFunc(
                [...officialPopResult.value, ...officialStickyResult.value],
                area.value,
                language.value,
                false
            );
        }
        return [];
    });
    return {
        officialTemplates,
        officialTemplatesLoading:
            officialPopupTemplatesV4Loading || officialStickybarTemplatesV4Loading,
        ownTemplatesLoading: ref(false),
        ownTemplates: ref([]),
        updateOwnTemplate: () => {},
        deleteOwnTemplate: () => {},
        officialPopupTemplatesV4OnResult,
        officialStickybarTemplatesV4OnResult
    };
};

export const usePopupTemplates = () => {
    const {
        officialPopupTemplatesV4Result,
        officialPopupTemplatesV4Loading,
        officialPopupTemplatesV4OnResult
    } = getOfficalPopup();
    const { ownPopupTemplatesV4Result, ownPopupTemplatesV4Loading, ownPopupTemplatesV4OnResult } =
        getOwnPopup();

    return {
        officialPopupTemplatesV4OnResult,
        ownPopupTemplatesV4OnResult
    };
};

export const useStickybarTemplates = () => {
    const {
        officialStickybarTemplatesV4Result,
        officialStickybarTemplatesV4Loading,
        officialStickybarTemplatesV4OnResult
    } = getOfficialStickybar();
    const {
        ownStickybarTemplatesV4Result,
        ownStickybarTemplatesV4Loading,
        ownStickybarTemplatesV4OnResult
    } = getOwnStickybar();
    return {
        officialStickybarTemplatesV4OnResult,
        ownStickybarTemplatesV4OnResult
    };
};
